<script lang="ts" setup>
	import type { FormRecordsContext } from '~/composables/useFormsFields'
	import type { Dayjs } from 'dayjs'

	interface Emits {
		(event: 'change', arg: unknown): void
		(event: 'update:value', arg: unknown): void
	}
	type Props = Omit<FormRecordsContext, 'key'>
	const props = defineProps<Props>()

	const emits = defineEmits<Emits>()
	const { filterOption } = useUtils()

	const filteredInput = computed(() => {
		const { rules, inputClass, wrapperClass, options, selectCls, type, ...rest } = props
		return rest
	})

	const attr = useAttrs()
	const filtereAttr = computed(() => (keysToExclude: string[]) => {
		const filtered = Object.fromEntries(Object.entries(attr).filter(([key]) => !keysToExclude.includes(key)))
		return filtered
	})

	const labelInfo = computed(() => {
		const label = props.label || ''
		const match = label.match(/^\s*<([a-z0-9-]+)/i)
		if (!match) return { isHtml: false, tagName: '', isLabelTag: false }
		const tagName = String(match?.[1]).toLowerCase()
		return { isHtml: true, tagName, isLabelTag: tagName === 'label' }
	})
	const { disablePreviousDate, disableFutureYrAndDayAfter, isDate18Min, minOf18 } = useCustomDate()

	const disabledDate = (currentDate: Dayjs) => {
		if (props.isDateEighteenPlus) {
			return isDate18Min(currentDate)
		}
		if (!props.canSelectDatesBeforeToday) {
			return disablePreviousDate(currentDate)
		}
		if (!props.canSelectDatesAfterToday) {
			return disableFutureYrAndDayAfter(currentDate)
		}
		return false
	}

	defineOptions({
		inheritAttrs: false
	})
</script>

<template>
	<div>
		<a-form-item
			:label="labelInfo.isHtml ? '' : label"
			:rules
			:name="form_name || name"
			:colon="false"
			:class="cn('!my-0', wrapperClass)"
		>
			<slot name="label" v-bind="props">
				<template v-if="labelInfo.isHtml">
					<label v-if="!labelInfo.isLabelTag" v-html="props.label" />
					<component :is="labelInfo.tagName || 'label'" v-else v-html="props.label" />
				</template>
			</slot>
			<slot :name="form_name">
				<a-select
					v-if="type === 'select' && Array.isArray(options)"
					:class="cn(selectCls, '!capitalize')"
					:filter-option
					v-bind="{ ...filteredInput, ...attr }"
					:options="options || []"
					:dropdown-style="{ textTransform: 'capitalize' }"
					:data-testid="form_name || name"
					:loading="loading"
					@change="e => [emits('change', e), emits('update:value', e), change?.(e)]"
				/>
				<a-date-picker
					v-if="type === 'date'"
					v-bind="{
						...filteredInput,
						...filtereAttr(['options']),
						disabledDate: filteredInput?.disabledDate || disabledDate,
						defaultPickerValue:
							filteredInput?.defaultPickerValue || isDateEighteenPlus ? minOf18 : undefined
					}"
					:class="cn('custom-input w-full !bg-gray-50 !px-5', inputClass)"
					format="DD MMM YYYY"
					value-format="YYYY-MM-DD"
					:data-testid="form_name || name"
					@change="e => [emits('change', e), emits('update:value', e), change?.(e)]"
				>
					<template #suffixIcon>
						<tools-icon dir="line" icon="calendar" :height="20" :width="20" stroke="#667085" />
					</template>
				</a-date-picker>

				<a-input
					v-if="type === 'text' || type === 'email'"
					v-bind="{ ...filteredInput, ...filtereAttr(['options']) }"
					:type
					:class="cn('custom-input w-full !bg-gray-50 !px-5', inputClass)"
					:data-testid="form_name || name"
					:loading="loading"
					:maxlength="rules?.max"
					@change="
						(e: unknown) => [
							emits('change', e?.target?.value || null),
							emits('update:value', e?.target?.value || ''),
							change?.(e)
						]
					"
				/>
				<a-input
					v-if="type === 'currency'"
					v-naira="true"
					v-bind="{ ...filteredInput, ...filtereAttr(['options']) }"
					inputmode="numeric"
					:class="cn('custom-input w-full !bg-gray-50 !px-5', inputClass)"
					:loading="loading"
					:data-testid="form_name || name"
					@change="
						(e: unknown) => [
							emits('change', e?.target?.value),
							emits('update:value', e?.target?.value || ''),
							change?.(e)
						]
					"
				/>
				<a-input
					v-if="type === 'number'"
					v-number="true"
					v-format="format"
					v-bind="{ ...filteredInput, ...filtereAttr(['options']) }"
					type="text"
					inputmode="numeric"
					:class="cn('custom-input w-full !bg-gray-50 !px-5', inputClass)"
					:loading="loading"
					:data-testid="form_name"
					@change="
						(e: unknown) => [
							emits('change', e?.target?.value),
							emits('update:value', e?.target?.value || ''),
							change?.(e)
						]
					"
				/>
				<form-item-password
					v-if="type === 'password'"
					:validate="validatePassword"
					v-bind="{ ...filteredInput, ...filtereAttr(['options']) }"
					@change="(e: string) => [emits('change', e), emits('update:value', e), change?.(e)]"
				/>
				<a-textarea
					v-if="type === 'textarea'"
					auto-size
					v-bind="{ ...filteredInput, ...filtereAttr(['options']) }"
					:class="cn('custom-input !min-h-[84px] w-full !bg-gray-50 !px-5 !py-3', inputClass)"
					:data-testid="form_name || name"
					@change="
						(e: unknown) => [
							emits('change', e?.target?.value),
							emits('update:value', e?.target?.value || ''),
							change?.(e)
						]
					"
				/>
				<shared-upload-input
					v-if="type === 'upload'"
					accept=".png,.jpg,.jpeg"
					class="mt-2 w-full"
					v-bind="filteredInput"
					@change="emits('change', $event)"
				/>
			</slot>
		</a-form-item>
		<slot :name="`${form_name}-description`">
			<p
				v-if="description"
				:class="cn('text-[12px] leading-[18px] text-gray-500', descriptionClass)"
				v-html="description"
			/>
		</slot>
	</div>
</template>
